import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "checkbox",
    "group",
    "row",
    "count",
    "search",
    "clearButton",
    "filterStatus",
    "filterText",
    "groupActions",
    "stickyBar",
    "groupHeader",
    "clearAllButton",
    "countWrapper",
    "selectGroupButton",
    "clearGroupButton",
    "scopeDescription",
  ];

  static values = {
    presets: { type: Object, default: {} },
    presetsDefaultLabel: { type: String, default: "" },
  };

  declare readonly checkboxTargets: HTMLInputElement[];
  declare readonly groupTargets: HTMLElement[];
  declare readonly rowTargets: HTMLElement[];
  declare readonly countTarget: HTMLElement;
  declare readonly searchTarget: HTMLInputElement;
  declare readonly clearButtonTarget: HTMLElement;
  declare readonly filterStatusTarget: HTMLElement;
  declare readonly filterTextTarget: HTMLElement;
  declare readonly groupActionsTargets: HTMLElement[];
  declare readonly stickyBarTarget: HTMLElement;
  declare readonly groupHeaderTargets: HTMLElement[];
  declare readonly hasCountTarget: boolean;
  declare readonly hasSearchTarget: boolean;
  declare readonly hasClearButtonTarget: boolean;
  declare readonly hasFilterStatusTarget: boolean;
  declare readonly hasFilterTextTarget: boolean;
  declare readonly hasStickyBarTarget: boolean;
  declare readonly clearAllButtonTarget: HTMLElement;
  declare readonly hasClearAllButtonTarget: boolean;
  declare readonly countWrapperTarget: HTMLElement;
  declare readonly hasCountWrapperTarget: boolean;
  declare readonly selectGroupButtonTargets: HTMLElement[];
  declare readonly clearGroupButtonTargets: HTMLElement[];
  declare presetsValue: Record<string, string>;
  declare presetsDefaultLabelValue: string;

  private stickyObserver?: IntersectionObserver;
  private resizeObserver?: ResizeObserver;
  private sentinel?: HTMLElement;
  private applyingPreset = false;
  private originalText = new WeakMap<HTMLElement, string>();

  connect() {
    this.cacheOriginalText();
    this.updateCount();
    this.syncGroupHeaderOffsets();
    this.observeSticky();
    this.observeStickyBarResize();
  }

  disconnect() {
    this.stickyObserver?.disconnect();
    this.resizeObserver?.disconnect();
    this.sentinel?.remove();
  }

  // Bulk actions apply to all scopes regardless of search filter.
  // Search is for finding and inspecting, not for scoping bulk operations.

  selectAllRead() {
    this.checkboxTargets.forEach((checkbox) => {
      checkbox.checked = checkbox.dataset.permission === "read";
    });
    this.updateCount();
  }

  selectAllReadWrite() {
    this.checkboxTargets.forEach((checkbox) => {
      const perm = checkbox.dataset.permission;
      checkbox.checked = perm === "read" || perm === "write";
    });
    this.updateCount();
  }

  selectAll() {
    this.checkboxTargets.forEach((checkbox) => {
      checkbox.checked = true;
    });
    this.updateCount();
  }

  clearAll() {
    this.checkboxTargets.forEach((checkbox) => {
      checkbox.checked = false;
    });
    this.updateCount();
  }

  applyPreset(event: Event) {
    const target = event.target as HTMLElement;
    const button = target.closest<HTMLElement>('[role="menuitem"]');
    if (!button) {
      return;
    }

    const label = button.getAttribute("data-menu-button-text") || button.textContent?.trim() || "";
    const presets = this.presetsValue as Record<string, string>;
    const preset = presets[label];

    this.applyingPreset = true;
    switch (preset) {
      case "read":
        this.selectAllRead();
        break;
      case "read_write":
        this.selectAllReadWrite();
        break;
      case "full":
        this.selectAll();
        break;
    }
    this.applyingPreset = false;
  }

  updateCount() {
    if (!this.hasCountTarget) {
      return;
    }
    const checked = this.checkboxTargets.filter((cb) => cb.checked).length;
    this.countTarget.textContent = String(checked);

    if (this.hasCountWrapperTarget) {
      this.countWrapperTarget.classList.toggle("hidden", checked === 0);
    }
    if (this.hasClearAllButtonTarget) {
      this.clearAllButtonTarget.classList.toggle("hidden", checked === 0);
    }

    if (!this.applyingPreset) {
      this.resetPresetLabel();
    }

    this.refreshAllGroupToggles();
  }

  private resetPresetLabel() {
    const menuButton = this.element.querySelector<HTMLElement>("[data-menu-button]");
    if (menuButton) {
      menuButton.textContent = this.presetsDefaultLabelValue;
    }
  }

  selectGroup(event: Event & { params: { area: string } }) {
    event.preventDefault();
    event.stopPropagation();
    const group = this.findGroup(event.params.area);
    if (!group) {
      return;
    }

    this.groupCheckboxes(group).forEach((cb) => {
      cb.checked = true;
    });
    this.updateCount();
  }

  clearGroup(event: Event & { params: { area: string } }) {
    event.preventDefault();
    event.stopPropagation();
    const group = this.findGroup(event.params.area);
    if (!group) {
      return;
    }

    this.groupCheckboxes(group).forEach((cb) => {
      cb.checked = false;
    });
    this.updateCount();
  }

  clearSearch() {
    if (!this.hasSearchTarget) {
      return;
    }
    this.searchTarget.value = "";
    this.search();
    this.searchTarget.focus();
  }

  searchKeydown(event: KeyboardEvent) {
    if (event.key === "Escape") {
      this.clearSearch();
    }
  }

  search() {
    if (!this.hasSearchTarget) {
      return;
    }
    const tokens = this.tokenize(this.searchTarget.value);

    if (this.hasClearButtonTarget) {
      this.clearButtonTarget.classList.toggle("hidden", tokens.length === 0);
    }

    this.restoreHighlights();

    const totalRows = this.rowTargets.length;

    if (tokens.length === 0) {
      this.rowTargets.forEach((row) => row.removeAttribute("hidden"));
      this.groupTargets.forEach((group) => group.removeAttribute("hidden"));
      this.updateFilterStatus(totalRows, totalRows, false);
      this.refreshAllGroupToggles();
      return;
    }

    let visibleCount = 0;

    this.groupTargets.forEach((group) => {
      let groupHasVisible = false;

      const rows = this.rowTargets.filter((row) => group.contains(row));
      rows.forEach((row) => {
        const label = (row.dataset.scopeLabel || "").toLowerCase();
        const match = tokens.every((token) => label.includes(token));
        row.toggleAttribute("hidden", !match);
        if (match) {
          groupHasVisible = true;
          visibleCount++;
        }
      });

      group.toggleAttribute("hidden", !groupHasVisible);
    });

    this.applyHighlights(tokens);

    this.updateFilterStatus(visibleCount, totalRows, visibleCount < totalRows);
    this.refreshAllGroupToggles();
  }

  private syncGroupHeaderOffsets() {
    if (!this.hasStickyBarTarget) {
      return;
    }

    const barHeight = this.stickyBarTarget.offsetHeight;
    this.groupHeaderTargets.forEach((header) => {
      header.style.top = `${barHeight}px`;
    });
  }

  private findGroup(area: string): HTMLElement | undefined {
    return this.groupTargets.find((el) => el.dataset.scopeMatrixAreaParam === area);
  }

  private groupCheckboxes(group: HTMLElement): HTMLInputElement[] {
    return Array.from(
      group.querySelectorAll<HTMLInputElement>(
        'input[type="checkbox"][data-scope-matrix-target="checkbox"]',
      ),
    );
  }

  private updateGroupToggle(group: HTMLElement, area: string) {
    const checkboxes = this.groupCheckboxes(group);

    const checkedCount = checkboxes.filter((cb) => cb.checked).length;
    const noneChecked = checkedCount === 0;
    const allChecked = checkboxes.length > 0 && checkedCount === checkboxes.length;

    const selectBtn = this.selectGroupButtonTargets.find(
      (el) => el.dataset.scopeMatrixAreaParam === area,
    );
    const clearBtn = this.clearGroupButtonTargets.find(
      (el) => el.dataset.scopeMatrixAreaParam === area,
    );

    if (selectBtn) {
      selectBtn.style.display = allChecked ? "none" : "";
    }
    if (clearBtn) {
      clearBtn.style.display = noneChecked ? "none" : "";
    }
  }

  private refreshAllGroupToggles() {
    this.groupTargets.forEach((group) => {
      const area = group.dataset.scopeMatrixAreaParam;
      if (area) {
        this.updateGroupToggle(group, area);
      }
    });
  }

  private updateFilterStatus(visible: number, total: number, active: boolean) {
    if (!this.hasFilterStatusTarget) {
      return;
    }

    if (active) {
      if (this.hasFilterTextTarget) {
        const template = this.filterTextTarget.dataset.template || "";
        this.filterTextTarget.textContent = template
          .replace("%{visible}", String(visible))
          .replace("%{total}", String(total));
      }
      this.filterStatusTarget.classList.remove("hidden");
    } else {
      this.filterStatusTarget.classList.add("hidden");
    }
  }

  private observeStickyBarResize() {
    if (!this.hasStickyBarTarget) {
      return;
    }

    this.resizeObserver = new ResizeObserver(() => {
      this.syncGroupHeaderOffsets();
    });

    this.resizeObserver.observe(this.stickyBarTarget);
  }

  private observeSticky() {
    if (!this.hasStickyBarTarget) {
      return;
    }

    this.sentinel = document.createElement("div");
    this.sentinel.style.height = "1px";
    this.sentinel.style.marginBottom = "-1px";
    this.stickyBarTarget.before(this.sentinel);

    this.stickyObserver = new IntersectionObserver(
      ([entry]) => {
        const isStuck = !entry.isIntersecting;
        this.stickyBarTarget.classList.toggle("border-gray", isStuck);
        this.stickyBarTarget.classList.toggle("border-transparent", !isStuck);
        this.stickyBarTarget.classList.toggle("shadow-xs", isStuck);
      },
      { threshold: 0 },
    );

    this.stickyObserver.observe(this.sentinel);
  }

  private tokenize(query: string): string[] {
    return query.toLowerCase().trim().split(/\s+/).filter(Boolean);
  }

  private highlightableEls(row: HTMLElement): HTMLElement[] {
    return Array.from(
      row.querySelectorAll<HTMLElement>(
        'span.semi-bold, code.code, [data-scope-matrix-target="scopeDescription"]',
      ),
    );
  }

  private cacheOriginalText() {
    this.rowTargets.forEach((row) => {
      this.highlightableEls(row).forEach((el) => {
        if (!this.originalText.has(el)) {
          this.originalText.set(el, el.textContent ?? "");
        }
      });
    });
  }

  private restoreHighlights() {
    this.rowTargets.forEach((row) => {
      this.highlightableEls(row).forEach((el) => {
        const original = this.originalText.get(el);
        if (original != null) {
          el.textContent = original;
        }
      });
    });
  }

  private applyHighlights(tokens: string[]) {
    if (tokens.length === 0) {
      return;
    }

    const pattern = tokens.map((token) => this.escapeRegExp(token)).join("|");
    const re = new RegExp(`(${pattern})`, "gi");

    this.rowTargets.forEach((row) => {
      if (row.hasAttribute("hidden")) {
        return;
      }

      this.highlightableEls(row).forEach((el) => {
        const original = this.originalText.get(el) ?? el.textContent ?? "";
        const fragment = document.createDocumentFragment();
        let lastIndex = 0;

        original.replace(re, (match: string, _group: string, offset: number) => {
          if (offset > lastIndex) {
            fragment.appendChild(document.createTextNode(original.slice(lastIndex, offset)));
          }
          const mark = document.createElement("mark");
          mark.className = "scope-matrix-mark";
          mark.appendChild(document.createTextNode(match));
          fragment.appendChild(mark);
          lastIndex = offset + match.length;
          return match;
        });

        if (lastIndex < original.length) {
          fragment.appendChild(document.createTextNode(original.slice(lastIndex)));
        }

        el.textContent = "";
        el.appendChild(fragment);
      });
    });
  }

  private escapeRegExp(str: string): string {
    return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  }
}
